package com.nonopn.skydome.common.app.pages.dashboard.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import com.nonopn.skywatcher.design.widgets.DefaultCard
import korlibs.time.DateTime
import kotlin.math.roundToInt
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.intensities_title
import com.nonopn.skywatcher.common.app.res.intensity_avg_negative
import com.nonopn.skywatcher.common.app.res.intensity_avg_positive
import com.nonopn.skywatcher.common.app.res.intensity_max_negative
import com.nonopn.skywatcher.common.app.res.intensity_max_positive
import org.jetbrains.compose.resources.stringResource

@Composable
fun MathReport(modifier: Modifier, mathReport: DashboardImpactReportV2.MathReport?) {
    InternalCard(
        modifier = modifier,
        title = stringResource(Res.string.intensities_title)
    ) {
        val separator = 6.dp

        Row(
            horizontalArrangement = Arrangement.spacedBy(separator)
        ) {
            MathReportChip(
                modifier = Modifier.weight(1.0f),
                color = DashboardColors.negativeColorCardTransparent,
                textColor = DashboardColors.negativeColor,
                title = stringResource(Res.string.intensity_max_negative),
                value = mathReport?.let { "${(it.minIntensity / 1000)} kA" } ?: "-",
                date = mathReport?.timestampMinSeconds?.let { DateTime.fromUnixMillis(it * 1000) }
            )

            MathReportChip(
                modifier = Modifier.weight(1.0f),
                color = DashboardColors.positiveColorCardTransparent,
                textColor = DashboardColors.positiveColor,
                title = stringResource(Res.string.intensity_max_positive),
                value = mathReport?.let { "${(it.maxIntensity / 1000)} kA" } ?: "-",
                date = mathReport?.timestampMaxSeconds?.let { DateTime.fromUnixMillis(it * 1000) }
            )
        }

        Spacer(modifier = Modifier.height(separator))

        Row(
            horizontalArrangement = Arrangement.spacedBy(separator)
        ) {
            MathReportChip(
                modifier = Modifier.weight(1.0f),
                color = DashboardColors.negativeColorCardTransparent,
                textColor = DashboardColors.negativeColor,
                title = stringResource(Res.string.intensity_avg_negative),
                value = mathReport?.let { "${(it.avgNegIntensity / 1000).roundToInt()} kA" } ?: "-",
            )

            MathReportChip(
                modifier = Modifier.weight(1.0f),
                color = DashboardColors.positiveColorCardTransparent,
                textColor = DashboardColors.positiveColor,
                title = stringResource(Res.string.intensity_avg_positive),
                value = mathReport?.let { "${(it.avgPosIntensity / 1000).roundToInt()} kA" } ?: "-",
            )
        }
    }
}

@Composable
private fun MathReportChip(
    modifier: Modifier,
    color: Color,
    textColor: Color,
    title: String,
    value: String,
    date: DateTime? = null
) {
    DefaultCard(
        modifier = modifier,
        backgroundColor = color,
        columnModifier = Modifier.padding(8.dp)
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = title,
                fontSize = 12.sp
            )

            Text(
                text = value,
                color = textColor,
                fontSize = 16.sp,
                fontWeight = FontWeight.ExtraBold
            )

            date?.let {
                Text(
                    text = "${date.dayOfMonth}/${date.month1}/${date.yearInt}",
                    fontSize = 12.sp
                )
            }
        }
    }
}

@Preview
@Composable
fun MathReportPreview() {
    MathReport(
        modifier = Modifier.width(500.dp),
        mathReport = DashboardImpactReportV2.MathReport(
            minIntensity = 333,
            maxIntensity = 98288,
            avgNegIntensity = 0.0,
            avgPosIntensity = 0.0,
            timestampMaxSeconds = 0,
            timestampMinSeconds = 0,
        )
    )
}
